<script setup lang="ts">
import {
  getSmallestThumbnailUrl,
  getTranslatedProperty,
} from "@shopware/helpers";
import { useTemplateRef } from "vue";
import type { Schemas } from "#shopware";

const props = defineProps<{ product: Schemas["Product"] }>();

const { product } = toRefs(props);
const { unitPrice, displayFrom } = useProductPrice(product);

const DEFAULT_THUMBNAIL_SIZE = 10;
const imageElement = useTemplateRef("imageElement");
const { width, height } = useElementSize(imageElement);

function roundUp(num: number) {
  return num ? Math.ceil(num / 100) * 100 : DEFAULT_THUMBNAIL_SIZE;
}

const srcPath = computed(() => {
  const biggestParam =
    width.value > height.value
      ? `width=${roundUp(width.value)}`
      : `height=${roundUp(height.value)}`;
  return `${getSmallestThumbnailUrl(
    product.value.cover?.media,
  )}?${biggestParam}&fit=crop,smart`;
});
</script>
<template>
  <div
    class="py-3 px-4 h-14 text-sm flex items-center gap-3  cursor-pointer transition duration-300"
  >
    <div
      class="rounded-md border border-grey-200 bg-grey-800 overflow-hidden flex-none"
    >
      <SharedImage
        ref="imageElement"
        loading="lazy"
        :src="product?.cover?.media?.url"
        :alt="product?.translated?.name ?? $t('mysofbed')"
        :height="65"
        :width="65"
        imgClass="h-12 w-12 object-contain bg-white"
        :sizes="{
          desktop: '65x65',
          tablet: '65x65',
          mobile: '65x65',
        }"
        format="webp"
        :mimeType="product?.cover?.media?.mimeType"
      />
    </div>
    <div class="flex items-center justify-between overflow-hidden gap-5 grow">
      <div
        data-testid="layout-search-suggest-name"
        class="text-secondary-500 whitespace-nowrap overflow-hidden text-ellipsis"
      >
        {{ getTranslatedProperty(product, "name") }}
      </div>
      <div class="flex-none text-right">
        <SharedPrice
          v-if="unitPrice"
          data-testid="layout-search-suggest-price"
          class="justify-end font-bold"
          :value="unitPrice"
        >
          <template #beforePrice>
            <span v-if="displayFrom">{{ $t("product.price.from") }}</span>
          </template>
        </SharedPrice>
        <ProductUnits
          data-testid="layout-search-suggest-units"
          :product="product"
          :show-content="false"
          class="text-3"
        />
      </div>
    </div>
  </div>
</template>
